import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { SyntheticEvent, useEffect } from 'react';
import { LocalizedTypography, LocalizedLabel, LocalizedMinorHighlight, TooltippedIconButton } from '@mews-commander/ui';
import { Banner, IconName, Position, SelectionCardGroup, Stack } from '@optimus-web/core';
import { FormCheckboxField, FormSelectField, LocalizedText, useToggle } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { isCurrentProfileAdmin } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useRolesAndPermissionsOptions, useCashiersOptions } from '../../hooks/useCommonFormOptions';
import { createListItems, hasDifferentRolesAndPermissions } from '../../utils';
import { EnterpriseType, LockedFormProps, RolesAndPermissionsOption } from '../../types';
import { useUserContext } from '../../context';
import { useIsUserProvisioned } from '../../hooks/useIsUserProvisioned';
import { getPrivileges, getRoleIds } from '../EditUser/formProps';
import { FieldNames } from '../FieldNames';
import { PrivilegesField } from './PrivilegesField';
import { RolesField } from './RolesField';

export const CommonFormFields = ({
    lockedFields,
    setLockedFields,
    fieldsConfig,
    detailedUser,
}: Partial<LockedFormProps>) => {
    const { enterpriseType } = useUserContext();
    const { setValue, resetField, watch } = useFormContext();
    const groupValue = watch(FieldNames.RolesAndPermissions);
    const isUserAdmin: boolean = useSelector(isCurrentProfileAdmin);
    const canAccessAllEnterprises = useSelector(hasPlatformScopeWithAccessAllEnterprises);

    const isUserProvisioned = useIsUserProvisioned(detailedUser);

    const rolesAndPermissionsOptions = useRolesAndPermissionsOptions();
    const cashiersOptions = useCashiersOptions();

    const optionGroupLockable = hasDifferentRolesAndPermissions(fieldsConfig || []);

    const optionGroupLocked = !!lockedFields?.[FieldNames.RolesAndPermissions];

    const setOptionGroupLocked = (locked: boolean) => {
        setLockedFields?.({ ...lockedFields, [FieldNames.RolesAndPermissions]: !locked });
        setValue(FieldNames.RolesAndPermissions, locked ? RolesAndPermissionsOption.Roles : undefined, {
            shouldDirty: true,
        });

        resetField(FieldNames.RoleIds, { defaultValue: locked ? [] : getRoleIds(detailedUser) });
        resetField(FieldNames.Privileges, { defaultValue: locked ? [] : getPrivileges(detailedUser) });
    };

    useEffect(() => {
        if (groupValue !== RolesAndPermissionsOption.Roles) {
            resetField(FieldNames.RoleIds);
        }

        if (groupValue !== RolesAndPermissionsOption.CustomizedPermissions) {
            resetField(FieldNames.Privileges);
        }

        if (groupValue === RolesAndPermissionsOption.NoPermissions) {
            resetField(FieldNames.ReceiveCustomerMessages);
        }
    }, [groupValue, setValue, resetField]);

    const handleRolesAndPermissionsChange = (event: SyntheticEvent<HTMLElement>, newValue: string) => {
        setValue(FieldNames.RolesAndPermissions, newValue, { shouldDirty: true });
    };

    const [showAdminWarning, toggleAdminWarning] = useToggle({ initialValue: true });

    return (
        <Stack vertical spacing="200">
            <Stack spacing="200" itemAlignment="center">
                <LocalizedMinorHighlight textKey={TextKey.RolesAndPermissions} />
                {optionGroupLockable ? (
                    <>
                        <TooltippedIconButton
                            icon={optionGroupLocked ? IconName.Lock : IconName.Unlock}
                            size="small"
                            tooltip={<LocalizedText textKey={TextKey.UserManagementUnlockField} />}
                            tooltipPosition={Position.Bottom}
                            onClick={() => setOptionGroupLocked(optionGroupLocked)}
                            type="button"
                        />
                        {optionGroupLocked ? (
                            <LocalizedTypography
                                textStyle="bodySmall"
                                textKey={TextKey.UserManagementValuesAreDifferentField}
                                variant="secondary"
                                as="div"
                            />
                        ) : null}
                    </>
                ) : null}
            </Stack>
            {isUserAdmin || canAccessAllEnterprises ? null : (
                <Banner
                    primaryText={
                        <LocalizedText
                            textKey={
                                detailedUser
                                    ? TextKey.UserManagementLimitedPermissionsEditBanner
                                    : TextKey.UserManagementLimitedPermissionsAddBanner
                            }
                        />
                    }
                    variant="info"
                />
            )}
            <SelectionCardGroup
                cardVariant="vertical"
                inputValue={optionGroupLocked ? undefined : groupValue}
                disabled={optionGroupLocked || isUserProvisioned}
                onChange={handleRolesAndPermissionsChange}
                options={rolesAndPermissionsOptions}
                type="radio"
                compact
                data-test-id="rolesAndPermissionsOptions"
            />
            {(groupValue === RolesAndPermissionsOption.CustomizedPermissions ||
                (optionGroupLocked && getPrivileges(detailedUser).length > 0)) && (
                <PrivilegesField disabled={optionGroupLocked || isUserProvisioned} />
            )}
            {(groupValue === RolesAndPermissionsOption.Roles ||
                (optionGroupLocked && getRoleIds(detailedUser).length > 0)) && (
                <RolesField disabled={optionGroupLocked || isUserProvisioned} detailedUser={detailedUser} />
            )}
            {groupValue === RolesAndPermissionsOption.Admin && showAdminWarning && (
                <Banner
                    primaryText={<LocalizedText textKey={TextKey.InviteUsersAdminWarningBannerPrimary} />}
                    secondaryText={<LocalizedText textKey={TextKey.InviteUsersAdminWarningBannerSecondary} />}
                    onClose={toggleAdminWarning}
                    variant="warning"
                />
            )}
            {(groupValue !== RolesAndPermissionsOption.NoPermissions || optionGroupLocked) && (
                <div>
                    <LocalizedLabel textKey={TextKey.AdvancedSettings} as="div" />
                    <FormCheckboxField
                        name={FieldNames.ReceiveCustomerMessages}
                        disabled={optionGroupLocked || isUserProvisioned}
                        indeterminate={optionGroupLocked && fieldsConfig?.includes('equalOptions')}
                        size="small"
                    >
                        <LocalizedText textKey={TextKey.ReceiveCustomerMessages} />
                    </FormCheckboxField>

                    {groupValue === RolesAndPermissionsOption.Admin && (isUserAdmin || canAccessAllEnterprises) && (
                        <FormCheckboxField
                            name={FieldNames.Reset2FA}
                            disabled={optionGroupLocked || isUserProvisioned}
                            indeterminate={optionGroupLocked && fieldsConfig?.includes('equalOptions')}
                            size="small"
                        >
                            <LocalizedText textKey={TextKey.ResetTwoFactor} />
                        </FormCheckboxField>
                    )}
                </div>
            )}
            {enterpriseType === EnterpriseType.Property && (
                <FormSelectField
                    name={FieldNames.Cashiers}
                    label={<LocalizedText textKey={TextKey.CashierPlural} />}
                    disabled={isUserProvisioned}
                    multiselect
                >
                    {createListItems(cashiersOptions)}
                </FormSelectField>
            )}
        </Stack>
    );
};
